
import {Component, Ref, Vue, Watch} from 'vue-property-decorator';
import TeacherConv from './TeacherConv.vue';
import TeacherIcon from './TeacherIcon.vue';
import TeacherChatBubble from './TeacherChatBubble.vue';
import TeacherListeningBadge from './TeacherListeningBadge.vue';
import TeacherMainButton from './TeacherMainButton.vue';
import TeacherSpeechRecognition from './TeacherSpeechRecognition.vue';
import {userService} from '../user/UserService';
import {isInMobileApp} from 'src/utils/MobileUtils';
import {analyticsController} from '../user/analytics/AnalyticsController';
import {availableGoals} from 'src/modules/user/models/UserConfigurationTypes';
import audioPlayer from 'src/modules/synth/AudioPlayer';
import {playerConfig} from '../player/models/PlayerConfig';
import {teacherService} from './TeacherService';
import {capturePage} from './PageCaptureUtils';
import {TeacherAction, ActionPayload} from './TeacherTypes';
import {introJsService} from 'src/modules/onboarding/intro/IntroJsService';
import StandaloneOnboardingSlide from 'src/modules/onboarding/slides/StandaloneOnboardingSlide.vue';
import KeyboardSlide from 'src/modules/onboarding/slides/content/KeyboardSlide.vue';
import {playerModals} from '../player/models/PlayerModals';
import * as Audio from '../audio/index';
import {isLandscapePage} from 'src/utils/PageSettingsUtils';

@Component({
  components: {
  TeacherSpeechRecognition,
  TeacherMainButton,
  TeacherListeningBadge,
  TeacherIcon,
  TeacherConv,
  TeacherChatBubble,
  StandaloneOnboardingSlide,
  KeyboardSlide,
  },
  })
export default class TeacherChatbot extends Vue {
  @Ref() readonly conv!: TeacherConv;
  @Ref() readonly speechRecognition!: TeacherSpeechRecognition;

  private isConvDisplayed: boolean = false;
  private userSawMessage: boolean = true;
  private isButtonShaking: boolean = false;
  private showActionButtons: boolean = false;
  private isListening: boolean = false;
  private isSpeaking: boolean = false;
  private SpeechRecognitionNotAvailable: boolean = false;
  private teacherMessageSent: boolean = false;
  private hasUnlockedOrientation: boolean = false;

  get teacherMessage() {
    return teacherService.state.teacherMessage;
  }

  get messages() {
    return teacherService.state.messages;
  }

  get timeWithNoResponse() {
    return teacherService.state.timeWithNoResponse;
  }

  get isChatButtonDisplayed() {
    const excludedRoutes = [
      'Subscribe Page',
      'StoreMobilePage',
      'Maintenance',
      'Offline',
      'Update',
      'download-partition',
      'SubscribeSuccessPage',
    ];
    const isChatButtonDisplayed =
      userService.user.id !== 0 &&
      ((this.isInPlayer &&
        (audioPlayer.state.playerStatus === 'pause' ||
          playerConfig.state.playerView === 'handprints' ||
          this.teacherMessage !== null)) ||
        (!this.isInPlayer && !excludedRoutes.includes(this.$route.name)));

    if (!isChatButtonDisplayed) {
      this.isConvDisplayed = false;
    }
    return isChatButtonDisplayed;
  }

  get isInPlayer() {
    return (
      this.$route.name === 'Player' ||
      this.$route.name === 'Player School' ||
      this.$route.name === 'Player Class' ||
      this.$route.name === 'Player Evaluation' ||
      this.$route.name === 'Player Challenge'
    );
  }

  get displayNewMessage() {
    const lastConnectionTime = new Date(userService.user.userActivityData.lastConnection).getTime();
    const currentTime = new Date().getTime();
    const correctSession =
      Math.abs(currentTime - lastConnectionTime) < 7200000 &&
      userService.user.userActivityData.eventCounts.connections % 4 === 0 &&
      userService.user.premium &&
      !this.isInPlayer;

    if (correctSession && !this.teacherMessageSent) {
      let text = '';
      userService.user.configurations.goals
        .sort(() => 0.5 - Math.random())
        .forEach((userGoal) => {
          const selectedGoal = availableGoals.find((goal) => goal.id === userGoal.id);
          if (selectedGoal) {
            if (selectedGoal.label === 'Jouer des morceaux et chanter en même temps') {
              return;
            }
            text += selectedGoal.label + ', ';
          }
        });
      if (text === '') {
        text = 'Apprendre le piano';
      }
      this.teacherMessageSent = true;
      this.userSawMessage = false;
      localStorage.setItem('lastTeacherMessage', String(Date.now()));
      this.sendRequest(text, true);
      this.shakeButton();
    }
    return correctSession && !this.userSawMessage && this.teacherMessageSent;
  }

  get isKeyboardOnboardingDisplayed() {
    return playerModals.state.activeModals.includes('keyboardSlide');
  }

  get isListeningNotes() {
    return Audio.musicTranscriptionController.state.workerState === 'ready';
  }

  mounted() {
    this.$root.$on('teacher_suggestion', this.newSuggestion);
    this.$root.$on('dismiss_teacher_suggestion', this.dismissSuggestion);
    this.$root.$on('displayKeyboardOnboarding', this.displayKeyboardOnboarding);
    this.$root.$on('open_teacher_action_buttons', this.showTeacherActionButtons);
    this.$root.$on('open_teacher_conversation', this.openConversation);

    this.setupEventListeners();

    teacherService.addMessage({
      message: 'Salut ! Je suis le prof de piano Tokatab, comment puis-je vous aider ?',
      fromUser: false,
      loading: false,
      generating: false,
      isHtml: true,
    });

    this.$nextTick(() => {
      this.capturePageContent();
    });
  }

  created() {
    this.teacherMessageSent =
      localStorage.getItem('lastTeacherMessage') &&
      new Date().toDateString() === new Date(Number(localStorage.getItem('lastTeacherMessage'))).toDateString();
  }

  beforeDestroy() {
    this.$root.$off('teacher_suggestion', this.newSuggestion);
    this.$root.$off('dismiss_teacher_suggestion', this.dismissSuggestion);
    this.$root.$off('displayKeyboardOnboarding', this.displayKeyboardOnboarding);
    this.$root.$off('open_teacher_action_buttons', this.showTeacherActionButtons);
    this.$root.$off('open_teacher_conversation', this.openConversation);

    this.removeEventListeners();
  }

  setupEventListeners() {
    teacherService.on(TeacherAction.REDIRECT, this.redirect);
    teacherService.on(TeacherAction.PAUSE, this.pause);
    teacherService.on(TeacherAction.PLAY, this.play);
    teacherService.on(TeacherAction.RESTART, this.restart);
    teacherService.on(TeacherAction.VALIDATE, this.validate);
    teacherService.on(TeacherAction.CHANGE_TEMPO, this.changeTempo);
    teacherService.on(TeacherAction.INCREASE_VOLUME, this.increaseVolume);
    teacherService.on(TeacherAction.DECREASE_VOLUME, this.decreaseVolume);
    teacherService.on(TeacherAction.TOGGLE_VOLUME, this.toggleVolume);
    teacherService.on(TeacherAction.SHOW_PLAYER_PARAMETER, this.showPlayerParameter);
    teacherService.on(TeacherAction.TOGGLE_LISTEN_MODE, this.toggleVoiceRecognition);
  }

  removeEventListeners() {
    teacherService.off(TeacherAction.REDIRECT, this.redirect);
    teacherService.off(TeacherAction.PAUSE, this.pause);
    teacherService.off(TeacherAction.PLAY, this.play);
    teacherService.off(TeacherAction.RESTART, this.restart);
    teacherService.off(TeacherAction.VALIDATE, this.validate);
    teacherService.off(TeacherAction.CHANGE_TEMPO, this.changeTempo);
    teacherService.off(TeacherAction.INCREASE_VOLUME, this.increaseVolume);
    teacherService.off(TeacherAction.DECREASE_VOLUME, this.decreaseVolume);
    teacherService.off(TeacherAction.TOGGLE_VOLUME, this.toggleVolume);
    teacherService.off(TeacherAction.SHOW_PLAYER_PARAMETER, this.showPlayerParameter);
    teacherService.off(TeacherAction.TOGGLE_LISTEN_MODE, this.toggleVoiceRecognition);
  }

  redirect(payload: ActionPayload) {
    this.$router.push('/' + payload.parameter);
  }

  pause() {
    audioPlayer.emit('pause');
  }

  play() {
    audioPlayer.emit('play');
  }

  restart() {
    audioPlayer.emit('loopRestart');
  }

  validate() {
    this.$root.$emit('evaluation_positive');
  }

  changeTempo(payload: ActionPayload) {
    this.$root.$emit('tempo_discrete' + payload.parameter);
  }

  increaseVolume(payload: ActionPayload) {
    playerConfig.changeVolume(payload.parameter, playerConfig.state.soundParameters.volumes[payload.parameter] + 15);
  }

  decreaseVolume(payload: ActionPayload) {
    playerConfig.changeVolume(payload.parameter, playerConfig.state.soundParameters.volumes[payload.parameter] - 15);
  }

  toggleVolume(payload: ActionPayload) {
    const isActive = !playerConfig.state.soundParameters.actives[payload.parameter];
    playerConfig.setVolumeActive(payload.parameter, isActive);
  }

  showPlayerParameter(payload: ActionPayload) {
    if (this.isInPlayer && (this.$q.screen.gt.sm || !this.isConvDisplayed)) {
      this.showPlayerParameterWithTour(payload.parameter as string);
    }
  }

  scrollBottom() {
    this.$nextTick(() => {
      if (this.isConvDisplayed) {
        this.conv.messageList.scrollTop = this.conv.messageList.scrollHeight;
      }
    });
  }

  sendText(text) {
    if (text !== '') {
      teacherService.sendText(text, this.isListening, this.isConvDisplayed, this.$route, this.isInPlayer);
      this.scrollBottom();
    }
  }

  sendRequest(text, fromTeacher = false) {
    teacherService.sendRequest(text, fromTeacher, this.isListening, this.isConvDisplayed, this.$route, this.isInPlayer);
    this.scrollBottom();
  }

  cancelMessage() {
    teacherService.cancelMessage();
  }

  @Watch('isConvDisplayed')
  onVisibilityChange(isConvDisplayed) {
    if (isInMobileApp() && this.isInPlayer) {
      if (isConvDisplayed) {
        // @ts-ignore
        this.MobileScreen.leaveFullScreenMode();
      }
      else {
        // @ts-ignore
        this.MobileScreen.enterFullScreenMode();
      }
    }

    if (isInMobileApp() && this.$q.screen.lt.md) {
      if (isConvDisplayed) {
        const isOnLandscapePage = isLandscapePage(this.$route.path);
        if (isOnLandscapePage) {
          // @ts-ignore
          this.MobileScreen.unlockScreenOrientation();
          this.hasUnlockedOrientation = true;
        }
      }
      else {
        if (this.hasUnlockedOrientation) {
          this.$root.$emit('forceCurrentPageOrientation', true);
          this.hasUnlockedOrientation = false;
        }
      }
    }
  }

  handleMainButtonClick() {
    analyticsController.logBasicAnalyticsEvent('user clicked on chatbot button', 'Teacher Chatbot', {
      page: this.$route.meta.title,
    });
    
    if (userService.user.premium) {
      this.showActionButtons = !this.showActionButtons;
    }
    else if (userService.user.configurations.currentWeeklyFreeTeacherMessages > 0) {
      // L'utilisateur a encore des messages gratuits disponibles
      this.showActionButtons = !this.showActionButtons;
    }
    else {
      // L'utilisateur a épuisé son quota de messages
      this.displayPremiumNotification();
    }
  }

  handleTextRecognized(text: string) {
    teacherService.addMessage({
      message: text,
      fromUser: true,
      loading: false,
      generating: false,
      isHtml: false,
    });
    this.sendRequest(text);
  }

  shakeButton() {
    setTimeout(async () => {
      for (let i = 0; i < 6; i++) {
        this.isButtonShaking = !this.isButtonShaking;
        await new Promise((resolve) => setTimeout(resolve, 1500));
      }
    }, 2000);
  }

  showPlayerParameterWithTour(parameterToShow: string) {
    if ((parameterToShow === 'displayFingerOrNotes' || parameterToShow === 'sheetMusicMode') && this.$q.screen.lt.md) {
      parameterToShow = 'MobileDisplayMenu';
    }
    if (parameterToShow === 'listenModeButton') {
      this.isConvDisplayed = false;
      this.showActionButtons = true;
    }
    setTimeout(() => introJsService.forceDisplayTour(['ShowParameterTour_' + parameterToShow]), 1000);
  }

  openConversation() {
    if(!this.showActionButtons) {
      analyticsController.logBasicAnalyticsEvent('user clicked on chatbot button', 'Teacher Chatbot', {
        page: this.$route.meta.title,
      });
    }
    
    if (userService.user.premium) {
      this.showActionButtons = false;
      this.isConvDisplayed = true;
      this.scrollBottom();
      if (this.messages.length === 0) {
        teacherService.addMessage({
          message: 'Salut ! Je suis le prof de piano Tokatab, comment puis-je vous aider ?',
          fromUser: false,
          loading: false,
          generating: false,
          isHtml: true,
        });
      }
      if (this.teacherMessageSent) {
        this.userSawMessage = true;
      }
    }
    else if (userService.user.configurations.currentWeeklyFreeTeacherMessages > 0) {
      // L'utilisateur a encore des messages gratuits disponibles
      this.showActionButtons = false;
      this.isConvDisplayed = true;
      this.scrollBottom();
      if (this.messages.length === 0) {
        teacherService.addMessage({
          message: 'Salut ! Je suis le prof de piano Tokatab, comment puis-je vous aider ?',
          fromUser: false,
          loading: false,
          generating: false,
          isHtml: true,
        });
      }
      if (this.teacherMessageSent) {
        this.userSawMessage = true;
      }
    }
    else {
      // L'utilisateur a épuisé son quota de messages
      this.displayPremiumNotification();
    }
  }

  toggleVoiceRecognition() {
    this.speechRecognition.toggleVoiceRecognition();
  }

  get chatBottom(): number {
    if (this.$q.screen.lt.md) {
      return 55;
    }
    else if (this.isInPlayer) {
      return 65;
    }
    return 0;
  }

  newSuggestion(suggestion) {
    teacherService.newSuggestion(suggestion);
  }

  dismissSuggestion(suggestion = null) {
    teacherService.dismissSuggestion(suggestion);
  }

  @Watch('$route')
  onRouteChange() {
    if (this.hasUnlockedOrientation) {
      this.hasUnlockedOrientation = false;
      this.$root.$emit('forceCurrentPageOrientation', true);
    }

    this.$nextTick(() => {
      setTimeout(() => {
        this.capturePageContent();
      }, 1000);
    });
  }

  capturePageContent() {
    try {
      capturePage(this.$route.name || '', this.$route.path || '', this.$route.meta?.title);
    }
    catch (error) {
      console.error('Erreur lors de la capture du contenu de la page:', error);
    }
  }

  finishKeyboardOnboarding() {
    const userOnboardingConfigCopy = JSON.parse(JSON.stringify(userService.user.configurations.onboardingConfig));
    userOnboardingConfigCopy.onboardingSlidesCompleted.push('keyboardSlide');
    userService.updateConfiguration('ONBOARDING', userOnboardingConfigCopy);
    this.closeKeyboardOnboarding();
  }

  activateListenMode() {
    this.finishKeyboardOnboarding();
    this.$root.$emit('activate_audio_capture');
  }

  displayKeyboardOnboarding() {
    playerModals.setIsModalDisplayed('keyboardSlide', true);
  }

  closeKeyboardOnboarding() {
    playerModals.setIsModalDisplayed('keyboardSlide', false);
  }

  showTeacherActionButtons() {
    this.isConvDisplayed = false;
    this.showActionButtons = true;
  }

  displayPremiumNotification() {
    this.$q.notify({
      timeout: 4000,
      message: 'Vous avez épuisé votre quota de messages avec le prof cette semaine. Abonnez-vous à Tokatab premium pour des messages illimités !',
      position: 'bottom',
      actions: [
        {
          label: "S'abonner",
          color: 'accent',
          handler: () => {
            this.$router.push('/subscribe');
          },
        },
      ],
    });
  }
}
